import { isDefined } from "@clipboard-health/util-ts";
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { z } from "zod";

import { type PushPermissionState } from "./usePushNotificationRegistration";

const PROMPT_STATE_STORAGE_KEY_PREFIX = "pushNotificationPromptState_";
// Legacy key name kept so markers written before the calendar->engagement-surface rename survive.
const HAS_VIEWED_ENGAGEMENT_SURFACE_KEY_PREFIX = "hasViewedCalendar_";

function getPromptStateKey(userId: string): string {
  return `${PROMPT_STATE_STORAGE_KEY_PREFIX}${userId}`;
}

function getEngagementSurfaceViewedKey(userId: string): string {
  return `${HAS_VIEWED_ENGAGEMENT_SURFACE_KEY_PREFIX}${userId}`;
}
const DEFERRAL_COOLDOWN_MS = 2 * 24 * 60 * 60 * 1000; // 2 days

export const PUSH_PROMPT_STATES = {
  not_prompted: "not_prompted",
  deferred: "deferred",
  granted: "granted",
  denied: "denied",
} as const;

export type PushPromptState = (typeof PUSH_PROMPT_STATES)[keyof typeof PUSH_PROMPT_STATES];

const storedPromptStateSchema = z.object({
  state: z.enum(["not_prompted", "deferred", "granted", "denied"]),
  dismissedAt: z.number().optional(),
});

interface StoredPromptState {
  state: PushPromptState;
  dismissedAt?: number;
}

function getStoredPromptState(userId: string): StoredPromptState {
  try {
    const stored = localStorage.getItem(getPromptStateKey(userId));
    if (!stored) {
      return { state: "not_prompted" };
    }

    return storedPromptStateSchema.parse(JSON.parse(stored));
  } catch {
    return { state: "not_prompted" };
  }
}

function setStoredPromptState(userId: string, state: StoredPromptState): void {
  try {
    localStorage.setItem(getPromptStateKey(userId), JSON.stringify(state));
  } catch {
    // localStorage may be unavailable
  }
}

// Reactive external store for hasViewedEngagementSurface so useSyncExternalStore
// picks up changes made by markEngagementSurfaceViewed() within the same session.
const engagementSurfaceViewedListeners = new Set<() => void>();

function subscribeToEngagementSurfaceViewed(callback: () => void): () => void {
  engagementSurfaceViewedListeners.add(callback);
  return () => {
    engagementSurfaceViewedListeners.delete(callback);
  };
}

function getEngagementSurfaceViewedSnapshot(userId: string): boolean {
  try {
    return localStorage.getItem(getEngagementSurfaceViewedKey(userId)) === "true";
  } catch {
    return false;
  }
}

/**
 * Records that the user has viewed a primary engagement surface (the daily-view
 * calendar for standard workplaces, the cases page for home-health workplaces).
 * Called from those pages to enable the push notification soft prompt trigger.
 */
export function markEngagementSurfaceViewed(userId: string): void {
  try {
    localStorage.setItem(getEngagementSurfaceViewedKey(userId), "true");
  } catch {
    // localStorage may be unavailable
  }

  for (const listener of engagementSurfaceViewedListeners) {
    listener();
  }
}

interface UsePushNotificationPromptStateParams {
  permissionState: PushPermissionState;
  workplaceUserId: string;
}

interface UsePushNotificationPromptStateResult {
  shouldShowPrompt: boolean;
  promptState: PushPromptState;
  onDismiss: () => void;
  onPermissionGranted: () => void;
  onPermissionDenied: () => void;
}

export function usePushNotificationPromptState(
  params: UsePushNotificationPromptStateParams
): UsePushNotificationPromptStateResult {
  const { permissionState, workplaceUserId } = params;

  const getSnapshot = useCallback(
    () => getEngagementSurfaceViewedSnapshot(workplaceUserId),
    [workplaceUserId]
  );
  const engagementSurfaceViewed = useSyncExternalStore(
    subscribeToEngagementSurfaceViewed,
    getSnapshot
  );

  const [storedState, setStoredState] = useState(() => getStoredPromptState(workplaceUserId));

  useEffect(() => {
    setStoredState(getStoredPromptState(workplaceUserId));
  }, [workplaceUserId]);

  const promptState = useMemo((): PushPromptState => {
    // Sync with actual permission state
    if (permissionState === "granted") {
      return "granted";
    }
    if (permissionState === "denied") {
      return "denied";
    }

    return storedState.state;
  }, [permissionState, storedState.state]);

  const shouldShowPrompt = useMemo((): boolean => {
    // Never show if permission is already resolved
    if (permissionState !== "prompt") {
      return false;
    }

    // Don't show until the user has viewed a primary engagement surface at least once
    if (!engagementSurfaceViewed) {
      return false;
    }

    // Never show again after grant or permanent denial
    if (storedState.state === "granted" || storedState.state === "denied") {
      return false;
    }

    // If deferred, check cooldown
    if (storedState.state === "deferred" && isDefined(storedState.dismissedAt)) {
      const elapsed = Date.now() - storedState.dismissedAt;
      return elapsed >= DEFERRAL_COOLDOWN_MS;
    }

    // "not_prompted" — show it
    return true;
  }, [engagementSurfaceViewed, permissionState, storedState]);

  const onDismiss = useCallback(() => {
    const newState: StoredPromptState = { state: "deferred", dismissedAt: Date.now() };
    setStoredPromptState(workplaceUserId, newState);
    setStoredState(newState);
  }, [workplaceUserId]);

  const onPermissionGranted = useCallback(() => {
    const newState: StoredPromptState = { state: "granted" };
    setStoredPromptState(workplaceUserId, newState);
    setStoredState(newState);
  }, [workplaceUserId]);

  const onPermissionDenied = useCallback(() => {
    const newState: StoredPromptState = { state: "denied" };
    setStoredPromptState(workplaceUserId, newState);
    setStoredState(newState);
  }, [workplaceUserId]);

  return {
    shouldShowPrompt,
    promptState,
    onDismiss,
    onPermissionGranted,
    onPermissionDenied,
  };
}
